import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { times } from '@sama/micro-dash';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Euler, Quaternion } from 'three';
import {
  ensureKeyframe,
  findInsertPoint,
  isKeyLocation,
} from '../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { Point3D } from '../../team-apis/otterdam/answers/shape-typings';
import {
  CuboidLocationState3D,
  isCuboidLocationState3D,
} from '../state/cuboid-location-state-3d';
import { ShapeLocationState3D } from '../state/shape-location-state-3d';
import { getLegalizedScale } from './point-cloud-utils';
import { roundValue } from './point-utils';

export function makeLocation$(
  frameNumberStore$: Observable<number>,
  keyLocationStore$: Observable<CuboidLocationState3D[]>,
) {
  return combineLatest([
    frameNumberStore$,
    keyLocationStore$.pipe(filter(isDefined)),
  ]).pipe(
    map(([frameNumber, keyLocations]) =>
      interpolateLocation(keyLocations, frameNumber),
    ),
  );
}

export function ensureKeyLocation<T extends ShapeLocationState3D>(
  keyLocationStore: Store<T[]>,
  frameNumber: number,
  specifiedLocation?: T,
) {
  return ensureKeyframe(
    keyLocationStore,
    frameNumber,
    (insertPoint) =>
      specifiedLocation ||
      interpolateLocation(keyLocationStore.state(), frameNumber, insertPoint),
  );
}

export function interpolateLocation<T extends ShapeLocationState3D>(
  locations: T[],
  frameNumber: number,
  insertPoint = findInsertPoint(locations, frameNumber),
) {
  if (isKeyLocation(locations, frameNumber, insertPoint)) {
    return locations[insertPoint];
  }

  const result = buildInitialLocation(locations, insertPoint, frameNumber);
  if (insertPoint > 0 && insertPoint < locations.length) {
    interpolate(locations, insertPoint, result);
  } else {
    if (insertPoint === 0) {
      result.visibility = 0;
    }
  }
  return result;
}

/**
 * This is what actually does the linear (inter/extra)polation.
 */
function setFittedAttributes(
  location1: ShapeLocationState3D,
  location2: ShapeLocationState3D,
  newLocation: ShapeLocationState3D,
) {
  const percentDelta =
    (newLocation.frame_number - location1.frame_number) /
    (location2.frame_number - location1.frame_number);
  fitPoints(newLocation, location1, location2, percentDelta);
}

function fitPoints(
  newLocation: ShapeLocationState3D,
  location1: ShapeLocationState3D,
  location2: ShapeLocationState3D,
  percentDelta: number,
) {
  const len1 = location1.points.length;
  const len2 = location2.points.length;
  newLocation.points = times(Math.max(len1, len2), (i) =>
    fitPoint(
      location1.points[Math.min(len1 - 1, i)],
      location2.points[Math.min(len2 - 1, i)],
      percentDelta,
    ),
  );
  // different from back-end: we interpolate position, rotation & scale
  // on front-end only
  if (
    isCuboidLocationState3D(newLocation) &&
    isCuboidLocationState3D(location1) &&
    isCuboidLocationState3D(location2)
  ) {
    fit3DLocationAttributes(newLocation, location1, location2, percentDelta);
  }
}

function fit3DLocationAttributes(
  newLocation: CuboidLocationState3D,
  location1: CuboidLocationState3D,
  location2: CuboidLocationState3D,
  percentDelta: number,
) {
  newLocation.position = fitPoint(
    location1.position,
    location2.position,
    percentDelta,
  );
  newLocation.rotation = interpolateRotation(
    location1.rotation,
    location2.rotation,
    percentDelta,
  );

  const newScale = fitPoint(location1.scale, location2.scale, percentDelta);
  newLocation.scale = getLegalizedScale(newScale);
}

function fitPoint(p1: Point3D, p2: Point3D, percentDelta: number) {
  return p1.map((coord, i) => fit(coord, p2[i], percentDelta)) as Point3D;
}

function fit(n1: number, n2: number, percentDelta: number) {
  return roundValue((n2 - n1) * percentDelta + n1);
}

/**
 * This should have all attributes correct for the result _except_
 * visibility and the points that will be interpolated. This is normally
 * controlled by the key location before `frameNumber`. If there isn't one,
 * then use the location after.
 */
function buildInitialLocation<T extends ShapeLocationState3D>(
  locations: T[],
  insertPoint: number,
  frameNumber: number,
) {
  return {
    ...locations[Math.min(insertPoint, locations.length - 1)],
    frame_number: frameNumber,
  };
}

function interpolate(
  locations: ShapeLocationState3D[],
  insertPoint: number,
  result: ShapeLocationState3D,
) {
  setFittedAttributes(
    locations[insertPoint - 1],
    locations[insertPoint],
    result,
  );
  result.visibility = locations[insertPoint - 1].visibility;
}

function interpolateRotation(p1: Point3D, p2: Point3D, percentDelta: number) {
  const resultQuaternion = new Quaternion();

  resultQuaternion.slerpQuaternions(
    new Quaternion().setFromEuler(new Euler(...p1)),
    new Quaternion().setFromEuler(new Euler(...p2)),
    percentDelta,
  );

  return new Euler()
    .setFromQuaternion(resultQuaternion)
    .toArray()
    .splice(0, 3) as Point3D;
}
